import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './PointeSenseDash.css';
import Header from '../../widgets/Header/Header';
import DashboardGrid from './DashboardGrid';
import WidgetSelector from './WidgetSelector';
import { useMultipleBluetooth } from '../../bluetooth/useBluetoothMulti';
import { useDeviceSettings } from '../../backend/useDeviceSettings';
import { useDashboardLayout } from '../../backend/useDashboardLayout';
import { useDataProcessing } from '../../processing/useDataProcessing';
import { SensorDataProvider } from '../../processing/SensorDataContext';

function PointeSenseDashboard({ session, currentUser }) {
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('pointesense-view-mode') || 'single';
  });

  const [selectedFoot, setSelectedFoot] = useState(() => {
    return localStorage.getItem('pointesense-selected-foot') || 'left';
  });

  const [showWidgetSelector, setShowWidgetSelector] = useState(false);

  const { deviceSettings, loading: settingsLoading } = useDeviceSettings(session?.user?.id || null);

  const bluetooth = useMultipleBluetooth(deviceSettings);
  const {
    devices,
    isConnecting,
    connectToDevice,
    disconnectDevice,
    setIsRecording,
    clearData,
    setEventCallbacks,
    startCalibration,
    getStatus,
    resetGain,
  } = bluetooth;

  // Process data once here — shared via context so children don't re-process
  const processing = useDataProcessing(
    devices?.left?.sensorValues || {},
    devices?.right?.sensorValues || {},
    { onPointe: devices?.left?.onPointe || false, imu: devices?.left?.imu },
    { onPointe: devices?.right?.onPointe || false, imu: devices?.right?.imu },
    { windowSize: 20, enableSmoothing: true, smoothingFactor: 0.3 }
  );

  const { layout, visibleWidgets, saveLayout, toggleWidget } = useDashboardLayout(
    session?.user?.id,
    viewMode
  );

  useEffect(() => { localStorage.setItem('pointesense-view-mode', viewMode); }, [viewMode]);
  useEffect(() => { localStorage.setItem('pointesense-selected-foot', selectedFoot); }, [selectedFoot]);

  useEffect(() => {
    setEventCallbacks('left', {
      onJump: (data) => console.log('🦘 Left jump!', data),
      onTurn: (data) => console.log('💫 Left turn!', data),
      onCalibration: (data) => console.log('🎯 Left calibration:', data),
    });
    setEventCallbacks('right', {
      onJump: (data) => console.log('🦘 Right jump!', data),
      onTurn: (data) => console.log('💫 Right turn!', data),
      onCalibration: (data) => console.log('🎯 Right calibration:', data),
    });
  }, [setEventCallbacks]);

  useEffect(() => {
    return () => {
      if (devices.left?.isConnected) disconnectDevice('left');
      if (devices.right?.isConnected) disconnectDevice('right');
    };
  }, []);

  const handleViewModeChange = useCallback((mode) => setViewMode(mode), []);
  const handleFootChange = useCallback((foot) => setSelectedFoot(foot), []);

  const isRecording = {
    left: devices.left?.isRecording || false,
    right: devices.right?.isRecording || false,
  };

  const handleToggleRecording = useCallback((foot, value) => {
    setIsRecording(foot, value);
  }, [setIsRecording]);

  const handleClearData = useCallback((foot) => {
    if (foot === 'both') {
      clearData('left');
      clearData('right');
    } else {
      clearData(foot);
    }
  }, [clearData]);

  const handleConnect = useCallback(async (foot) => {
    try {
      await connectToDevice(foot);
    } catch (error) {
      console.error(`Failed to connect ${foot} foot:`, error);
      alert(`Failed to connect: ${error.message}`);
    }
  }, [connectToDevice]);

  const handleDisconnect = useCallback((foot) => {
    disconnectDevice(foot);
  }, [disconnectDevice]);

  // Shared context value — computed once, consumed by all widgets
  const sensorContextValue = useMemo(() => ({
    leftFoot: {
      pressure: processing.leftSensorValues,
      imu: devices.left?.imu,
      onPointe: devices.left?.onPointe || false,
      hotspots: processing.leftHotspots,
    },
    rightFoot: {
      pressure: processing.rightSensorValues,
      imu: devices.right?.imu,
      onPointe: devices.right?.onPointe || false,
      hotspots: processing.rightHotspots,
    },
    statistics: processing.statistics,
    session: {
      isRecording: isRecording.left || isRecording.right,
      timeOnPointe: processing.timeOnPointe,
      jumps: processing.jumps,
      turns: processing.turns,
    },
    mode: viewMode,
    selectedFoot,
  }), [
    processing.leftSensorValues,
    processing.rightSensorValues,
    processing.leftHotspots,
    processing.rightHotspots,
    processing.statistics,
    processing.timeOnPointe,
    processing.jumps,
    processing.turns,
    devices.left?.imu,
    devices.left?.onPointe,
    devices.right?.imu,
    devices.right?.onPointe,
    isRecording.left,
    isRecording.right,
    viewMode,
    selectedFoot,
  ]);

  // Props injected into each widget by DashboardGrid
  const widgetProps = useMemo(() => ({
    // Single foot
    pressure: devices[selectedFoot]?.sensorValues || {},
    foot: selectedFoot,
    onPointe: devices[selectedFoot]?.onPointe || false,
    // Dual foot
    leftPressure: processing.leftSensorValues,
    rightPressure: processing.rightSensorValues,
    leftOnPointe: devices.left?.onPointe || false,
    rightOnPointe: devices.right?.onPointe || false,
    stats: processing.statistics,
    isRecording: isRecording.left || isRecording.right,
  }), [
    selectedFoot, devices, processing.leftSensorValues, processing.rightSensorValues,
    processing.statistics, isRecording.left, isRecording.right
  ]);

  if (settingsLoading) {
    return (
      <div className="pointesense-dashboard loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <span>Loading device settings...</span>
        </div>
      </div>
    );
  }

  return (
    <SensorDataProvider value={sensorContextValue}>
      <div className="pointesense-dashboard">
        <Header
          devices={devices}
          isConnecting={isConnecting}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          isRecording={isRecording}
          handleToggleRecording={handleToggleRecording}
          handleClearData={handleClearData}
          currentUser={currentUser}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          selectedFoot={selectedFoot}
          onFootChange={handleFootChange}
          onWidgetSelectorToggle={() => setShowWidgetSelector(prev => !prev)}
        />

        <div className="dashboard-content-area" style={{ position: 'relative' }}>
          {showWidgetSelector && (
            <WidgetSelector
              mode={viewMode}
              visibleWidgets={visibleWidgets}
              onToggle={toggleWidget}
              onClose={() => setShowWidgetSelector(false)}
            />
          )}

          <DashboardGrid
            layout={layout}
            visibleWidgets={visibleWidgets}
            onLayoutChange={(newLayout) => saveLayout(newLayout)}
            widgetProps={widgetProps}
          />
        </div>
      </div>
    </SensorDataProvider>
  );
}

export default PointeSenseDashboard;
